import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListFilterSelect, ListPagination,
} from '../components/ui/ListPageChrome';
import { useTableSort, SortTh } from '../components/ui/TableSort';

const STATUS_COLORS = {
  Draft:     'bg-[#F0F4F3] text-[#6B7F7F]',
  Open:      'bg-[#EDF5F0] text-[#4A9E6E]',
  Closed:    'bg-[#EDF2F7] text-[#4A6FA5]',
  Cancelled: 'bg-[#FDF0EE] text-[#C4574B]',
};

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [rows, setRows]       = useState([]);
  const sort = useTableSort(rows);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const limit = 25;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit });
        if (search) params.set('search', search);
        if (statusFilter) params.set('status', statusFilter);
        const data = await api.get(`/purchase-orders?${params}`);
        if (!cancelled) {
          setRows(data.rows || []);
          setTotal(data.total || 0);
          setSelectedIds(new Set());
          setIsAllSelected(false);
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, search, statusFilter, refreshKey]);

  const totalPages = Math.ceil(total / limit) || 1;
  const resetPage = () => setPage(1);

  const handleSelectRow = (id) => {
    if (isAllSelected) {
      const next = new Set();
      rows.forEach(r => {
        if (r.po_id !== id) {
          next.add(r.po_id);
        }
      });
      setSelectedIds(next);
      setIsAllSelected(false);
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const pageAllSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.po_id));
    if (isAllSelected || pageAllSelected) {
      setIsAllSelected(false);
      setSelectedIds(new Set());
    } else {
      setIsAllSelected(true);
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = isAllSelected ? total : selectedIds.size;
    if (count === 0) return;
    const msg = `Are you sure you want to delete the ${count} selected purchase order(s)? This will also delete their associated lines.`;
    if (!window.confirm(msg)) return;

    try {
      if (isAllSelected) {
        await api.post('/purchase-orders/bulk-delete', {
          all: true,
          search: search || undefined,
          status: statusFilter || undefined
        });
      } else {
        await api.post('/purchase-orders/bulk-delete', {
          ids: Array.from(selectedIds)
        });
      }
      setSelectedIds(new Set());
      setIsAllSelected(false);
      
      const newTotal = total - count;
      const newMaxPages = Math.ceil(newTotal / limit) || 1;
      if (page > newMaxPages) {
        setPage(newMaxPages);
      } else {
        setRefreshKey(prev => prev + 1);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete purchase orders');
    }
  };

  const displayCount = isAllSelected ? total : selectedIds.size;

  return (
    <ListPageShell>
      <ListPageHeader
        title="Purchase Orders"
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && displayCount > 0 && (
              <button type="button" onClick={handleBulkDelete} className="btn-danger">
                Delete Selected ({displayCount})
              </button>
            )}
            <button type="button" onClick={() => navigate('/purchase-order/new')} className="btn-primary">
              + Add New
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 items-center">
        <ListSearchBar
          value={search}
          onChange={setSearch}
          onChangeExtra={resetPage}
          placeholder="Search PO number or vendor…"
        />
        <ListFilterSelect value={statusFilter} onChange={setStatusFilter} onChangeExtra={resetPage}>
          <option value="">All Statuses</option>
          {['Draft', 'Open', 'Closed', 'Cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </ListFilterSelect>
      </div>

      <div className="data-table-wrap overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              {isSuperAdmin && (
                <th className="text-center px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected || (rows.length > 0 && rows.every(r => selectedIds.has(r.po_id)))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                  />
                </th>
              )}
              {[
                ['PO Number', 'po_number'],
                ['Vendor', 'vendor_name'],
                ['Document Series', 'department'],
                ['Approver', 'approver_name'],
                ['Posting Date', 'posting_date'],
                ['Delivery Date', 'delivery_date'],
                ['Total', 'line_total'],
                ['Status', 'status'],
                ['Time-Bound', 'is_time_bound'],
              ].map(([h, field]) => (
                <SortTh
                  key={h}
                  sort={sort}
                  field={field}
                  className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider whitespace-nowrap"
                >
                  {h}
                </SortTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuperAdmin ? 10 : 9} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? 10 : 9} className="px-4 py-8 text-center text-sap-textLight">No purchase orders found.</td></tr>
            ) : sort.rows.map((r) => (
              <tr key={r.po_id}
                onClick={() => navigate(`/purchase-order/${r.po_id}`)}
                className={`table-row even:bg-gray-50/50 ${r.is_time_bound ? 'border-l-4 border-l-amber-400' : ''}`}>
                {isSuperAdmin && (
                  <td className="px-4 py-3 text-center w-12" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isAllSelected || selectedIds.has(r.po_id)}
                      onChange={() => handleSelectRow(r.po_id)}
                      className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-sap-blue">{r.po_number}</td>
                <td className="px-4 py-3">
                  <span className="font-medium">{r.vendor_code}</span>
                  <span className="ml-1 text-sap-textLight">{r.vendor_name}</span>
                </td>
                <td className="px-4 py-3 text-sap-text">{r.department?.trim() ? r.department : '—'}</td>
                <td className="px-4 py-3 text-sap-text font-medium">{r.approver_name?.trim() ? r.approver_name : '—'}</td>
                <td className="px-4 py-3 text-sap-textLight">{r.posting_date?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-sap-textLight">{r.delivery_date?.slice(0, 10) || '—'}</td>
                <td className="px-4 py-3 text-right font-semibold">
                  {Number(r.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3">
                  {r.status === 'Open' ? (
                    <span className="badge-active">{r.status}</span>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${STATUS_COLORS[r.status] || ''}`}>{r.status}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {r.is_time_bound ? (
                    <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-700">Yes</span>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListPagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
    </ListPageShell>
  );
}
